import React, { useEffect, useState } from 'react'
import { TrackedLink } from '@flock/shared-ui'
import { localStore, sessionStore } from '@flock/utils'
import * as Sentry from '@sentry/gatsby'
import { Alert, styled, SxProps, Typography } from '@mui/material'
import { OFFER_PAGE_PATH } from '@flock/shared-ui/src/routeConstants'
import {
  CONTENTFUL_ACCESS_TOKEN,
  CONTENTFUL_ENTRIES_URL,
} from '../../constants'
import { track } from '../../utils/analytics'
import RedoArrow from '../../images/redo-arrow.svg'

type BannerData = {
  bannerText: string
  url: string
}

type BannerItem = {
  sys: { createdAt: string }
  fields: BannerData
}

type BannerAPIResponse = {
  items: BannerItem[]
}

const formatBannerData = (response: BannerAPIResponse) => {
  const items = response?.items

  const mostRecentItem = items?.sort((a: any, b: any) => {
    const timeA = new Date(a.sys.createdAt).getTime()
    const timeB = new Date(b.sys.createdAt).getTime()
    return timeB - timeA
  })[0]

  const bannerData: BannerData = {
    bannerText: mostRecentItem?.fields?.bannerText,
    url: mostRecentItem?.fields?.url,
  }

  return bannerData
}

type PageBannerProps = {
  bannerText?: string
  bannerCtaText?: string
  bannerUrl?: string
  setBannerOpen: (open: boolean) => void
  bannerSx?: SxProps<any>
  fontSx?: SxProps<any>
}

const Image = styled('img')({
  width: '16px',
  height: '16px',
  paddingRight: '8px',
})

const PageBanner = (props: PageBannerProps) => {
  const {
    bannerCtaText,
    bannerText: defaultBannerText,
    bannerUrl: defaultBannerUrl,
    setBannerOpen,
    bannerSx,
    fontSx,
  } = props

  const [bannerText, setBannerText] = useState(defaultBannerText)
  const [bannerUrl, setBannerUrl] = useState(defaultBannerUrl)
  const [completedLead, setCompletedLead] = useState(false)
  const [addressText, setAddressText] = useState('')
  const [loading, setLoading] = useState(true) // for handling async loading state

  useEffect(() => {
    const completedLeadParams = localStore?.getItem('completedLeadParams')
    const blogBannerParams = localStore?.getItem('blogBannerParams')

    if (completedLeadParams) {
      const parsedCompletedLeadParams = JSON.parse(completedLeadParams)
      if (
        parsedCompletedLeadParams?.leadUuid &&
        parsedCompletedLeadParams?.streetAddress &&
        parsedCompletedLeadParams?.streetNumber
      ) {
        if (
          parsedCompletedLeadParams?.streetAddress === 'S Ulster St' &&
          parsedCompletedLeadParams?.streetNumber === '3442'
        ) {
          setBannerText('Return to your valuation page')
          setAddressText('')
        } else {
          setBannerText(`Return to your valuation page for `)
          setAddressText(
            `${parsedCompletedLeadParams?.streetNumber} ${parsedCompletedLeadParams?.streetAddress}`
          )
        }
        setBannerUrl(
          `${OFFER_PAGE_PATH}/${parsedCompletedLeadParams?.leadUuid}`
        )
        setCompletedLead(true)
        setLoading(false)

        sessionStore.setItem('bannerOpen', 'true')
        setBannerOpen(true)
      }
    } else {
      // Set banner initially from local storage
      if (blogBannerParams) {
        const parsedBlogBannerParams = JSON.parse(blogBannerParams)
        if (parsedBlogBannerParams?.bannerText && parsedBlogBannerParams?.url) {
          setBannerText(parsedBlogBannerParams?.bannerText)
          setBannerUrl(parsedBlogBannerParams?.url)
          setLoading(false)
          sessionStore.setItem('bannerOpen', 'true')
          setBannerOpen(true)
        }
      }

      // Update the banner async as needed, and reset the local storage
      const fetchBannerData = async () => {
        try {
          const urlWithToken = `${CONTENTFUL_ENTRIES_URL}?access_token=${CONTENTFUL_ACCESS_TOKEN}&content_type=landingBanner`
          const response = await fetch(urlWithToken) // Replace with your actual API endpoint

          if (!response.ok) {
            throw new Error('Non-OK response fetching banner data from CMS')
          }

          const result = await response.json()
          if (!result?.items?.length) {
            throw new Error('Failed to fetch CMS data')
          }

          const formattedBannerData = formatBannerData(result)
          setBannerText(formattedBannerData?.bannerText)
          setBannerUrl(formattedBannerData?.url)
        } catch (error) {
          Sentry.captureException(error)
        } finally {
          // Update locals storage with the new banner data
          localStore.setItem(
            'blogBannerParams',
            JSON.stringify({ bannerText, url: bannerUrl })
          )
        }
      }

      fetchBannerData()
    }
  }, [])

  const onCloseBanner = (e: React.SyntheticEvent<Element, Event>) => {
    sessionStore.setItem('bannerOpen', 'false')
    e.stopPropagation()
    setBannerOpen(false)
  }

  return (
    <>
      {!loading && (
        <Alert
          onClose={onCloseBanner}
          action={
            completedLead ? (
              <Image src={RedoArrow} alt="Redo arrow" />
            ) : undefined
          }
          onClick={() => {
            track('banner-click', { type: 'link', url: bannerUrl })
            window.open(bannerUrl!, '_blank')
          }}
          sx={{ '&:hover': { cursor: 'pointer' }, ...bannerSx }}
        >
          <Typography variant="p2" sx={{ ...fontSx }}>
            {bannerText}{' '}
            <TrackedLink
              alt="Page Banner URL"
              aria-label="Learn more about our banner content"
              to={bannerUrl}
            >
              {bannerCtaText}
            </TrackedLink>
            {completedLead && (
              <Typography sx={{ fontWeight: '500' }}> {addressText}</Typography>
            )}
          </Typography>
        </Alert>
      )}
    </>
  )
}

PageBanner.defaultProps = {
  bannerText: `Read our latest post - Mortgage Rates Outlook: Trends, Projections, and Housing Implications`,
  bannerCtaText: '',
  bannerUrl: `https://flockhomes.com/blog/mortgage-rates-outlook-trends-projections-implications`,
  bannerSx: null,
  fontSx: null,
}

export default PageBanner
